/** Commissioner-facing review copy — single source for VOD/archive review UI. */

export const GAME_VERIFICATION_TITLE = 'Game verification';
export const GAME_VERIFICATION_STATUS_LABEL = 'Game verification';

export const TRUTH_VERIFICATION_COPY =
  'LSC reads gamertags from the scoreboard area only. It ignores menus, profiles, and on-field names.';

export const CHECK_SCOREBOARD_BUTTON_LABEL = 'Check scoreboard & gamertags';
export const CHECK_SCOREBOARD_VERIFYING_LABEL = 'Checking...';
export const CHECK_SCOREBOARD_ASYNC_PROGRESS_LABEL =
  'Checking scoreboard and gamertags in the background. Results will appear here automatically.';
export const VOD_TRUTH_VERIFICATION_QUEUE_LABEL = 'Verifying match footage...';
export const CHECK_SCOREBOARD_REVERIFY_LABEL = 'Check again';
export const CHECK_SCOREBOARD_HELPER_COPY =
  'Samples the VOD scoreboard. It does not watch gameplay.';

export const WHAT_LSC_CHECKED_LABEL = 'What LSC checked';
export const SCHEDULE_MATCH_STATUS_LABEL = 'Schedule match status';
export const MATCH_STRENGTH_LABEL = 'Match strength';
export const HOME_AWAY_ALIGNMENT_LABEL = 'Home & away alignment';
export const WHY_THIS_MATCHED_LABEL = 'Why this matched';
export const WHY_THIS_MATCHED_HELPER =
  'Signals that helped LSC suggest this scheduled game from the stream title and metadata.';
export const NO_MATCH_REASONS_YET = 'No match reasons yet';
export const SCHEDULED_GAME_LABEL = 'Scheduled game';

export const METADATA_MATCHER_BUTTON_LABEL = 'Match from title & stream info';
export const METADATA_MATCHER_RUNNING_LABEL = 'Matching from title & stream info...';
export const METADATA_MATCHER_HELPER_COPY =
  'Title and stream-info matching is a fallback. For best results, run Check scoreboard & gamertags first.';
export const METADATA_MATCHER_UNVERIFIED_WARNING =
  'Game verification has not run yet. Title-only matching may be incomplete.';
export const METADATA_MATCHER_ADVANCED_LABEL = 'Advanced: match from title & stream info';
export const METADATA_MATCHER_ADVANCED_HELPER =
  'Use this only when game verification or schedule binding cannot find the correct league match.';

export const FRAME_EVIDENCE_EXPAND_LABEL = 'View more scoreboard details';
export const FRAME_EVIDENCE_COLLAPSE_LABEL = 'Hide scoreboard details';

export const ADVANCED_TECHNICAL_DETAILS_LABEL = 'Advanced: technical details';
export const ADVANCED_TECHNICAL_DETAILS_SHOW = 'Show';
export const ADVANCED_TECHNICAL_DETAILS_HIDE = 'Hide';

export const DETECTED_GAMERTAGS_LABEL = 'Detected gamertags';
export const DETECTED_TEAMS_LABEL = 'Detected teams';
export const ROSTER_MISMATCH_LABEL = 'Roster mismatch';

export const LINK_GAMERTAG_PANEL_TITLE = 'Link gamertag to roster member';
export const LINK_GAMERTAG_PANEL_HELPER =
  'Confirm which roster member owns an unreadable gamertag for this VOD only. This does not link Twitch or Discord accounts.';
export const UNRESOLVED_GAMERTAGS_LABEL = 'Gamertags LSC could not match';

export const HANDOFF_ATTESTATION_TITLE = 'Confirm game without scoreboard tags';
export const HANDOFF_ATTESTATION_HELPER =
  'No scoreboard gamertags were extracted. You may confirm this VOD belongs to the verified handoff match based on the scoreboard from the live stream, team names on screen, and schedule binding.';
export const CONFIRM_RIGHT_GAME_LABEL = 'Confirm this is the right game';
export const CONFIRM_RIGHT_GAME_MODAL_TITLE = 'Confirm this is the right game?';
export const HANDOFF_ATTESTATION_EVIDENCE_INTRO =
  'Commissioner confirmed this VOD belongs to the verified handoff match despite no scoreboard tags being extracted.';

export const CROSS_LEAGUE_CONFLICT_TITLE = 'Players may belong to another league';

export const REJECT_VOD_MODAL_TITLE = 'Reject this VOD?';
export const REJECT_VOD_MODAL_BODY =
  'This removes the VOD from the active review queue. It will not be published to the league archive.';
export const REJECT_VOD_MODAL_CONFIRM = 'Reject VOD';
export const REJECT_VOD_MODAL_CANCEL = 'Cancel';

export const DISMISS_HELPER_COPY =
  'Dismiss removes this suggestion from your queue without marking the stream as rejected.';

export const BACKFILL_ASSISTANT_SAFETY_BANNER =
  'Backfill Assistant can soft-ignore cleanup candidates. It does not publish, approve, reject, or dismiss suggestions.';

export const BACKFILL_FILTER_LABEL = 'Filter Backfill candidates';
export const BACKFILL_FILTER_PLACEHOLDER = 'Filter by VOD ID, title, or broadcaster';
export const BACKFILL_FILTER_EMPTY_MESSAGE = 'No candidates match this filter.';
export const BACKFILL_SHOW_MORE_BUTTON = 'Show 10 more';

export function formatBackfillShowingPartial(visible: number, total: number): string {
  return `Showing ${visible} of ${total}`;
}

export function formatBackfillShowingAll(total: number): string {
  return `Showing all ${total}`;
}

export const IGNORE_VOD_MODAL_TITLE = 'Ignore this VOD candidate?';
export const IGNORE_VOD_MODAL_BODY =
  'This removes the VOD from active cleanup queues without rejecting, approving, or publishing it. You can still find it in Backfill under Already handled.';
export const IGNORE_VOD_MODAL_SAFETY_BULLETS = [
  'Review status will stay unchanged.',
  'Archive status will stay unchanged.',
  'No archive will be created.',
  'Verified live handoff VODs cannot be ignored.',
] as const;
export const IGNORE_VOD_REASON_LABEL = 'Why are you ignoring this candidate?';
export const IGNORE_VOD_REASON_PLACEHOLDER = 'No reason selected';
export const IGNORE_VOD_NOTE_LABEL = 'Note';
export const IGNORE_VOD_NOTE_PLACEHOLDER =
  'Add context for your future self or another commissioner.';
export const IGNORE_VOD_MODAL_CONFIRM = 'Ignore candidate';
export const IGNORE_VOD_MODAL_CANCEL = 'Cancel';
export const IGNORE_VOD_MODAL_LOADING = 'Ignoring...';
export const IGNORE_VOD_SUCCESS_BANNER =
  'Candidate ignored. It moved to Already handled.';
export const IGNORE_VOD_ROW_ACTION = 'Ignore candidate';

export const IGNORE_VOD_REASON_OPTIONS = [
  { value: 'not_league_game', label: 'Not league gameplay' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'stale', label: 'Stale / no longer useful' },
  { value: 'wrong_broadcaster', label: 'Wrong broadcaster' },
  { value: 'wrong_league', label: 'Wrong league' },
  { value: 'too_short', label: 'Too short' },
  { value: 'test_stream', label: 'Test stream' },
  { value: 'other', label: 'Other' },
] as const;

export const IGNORED_CANDIDATE_BANNER_TITLE = 'Ignored cleanup candidate';
export const IGNORED_CANDIDATE_BANNER_BODY =
  'This VOD was soft-ignored from active cleanup queues. It was not rejected, approved, or published.';
export const IGNORED_CANDIDATE_REASON_LABEL = 'Reason';
export const IGNORED_CANDIDATE_NOTE_LABEL = 'Note';
export const IGNORED_CANDIDATE_IGNORED_AT_LABEL = 'Ignored at';
export const IGNORED_CANDIDATE_IGNORED_BY_LABEL = 'Ignored by';
export const IGNORED_CANDIDATE_IGNORED_BY_VALUE = 'commissioner';
export const IGNORED_CANDIDATE_BANNER_FOOTER =
  'You can still view this candidate for reference. It appears in Backfill under Already handled.';
export const IGNORED_CANDIDATE_BACKFILL_LINK = 'Back to Backfill Assistant';
export const IGNORED_CANDIDATE_STATUS_LABEL = 'Ignored · Already handled';

export const REVIEW_ACTIONS_RESOLVE_IDENTITY_CTA = 'Resolve gamertag in game verification';
export const REVIEW_ACTIONS_HANDOFF_CTA = 'Confirm game without scoreboard tags in game verification';

export const TRUTH_REVIEW_LOCKED_COPY =
  'Run game verification first. Stream Center needs to read scoreboard gamertags, teams, and schedule evidence before this VOD can be reviewed.';

export const EMPTY_HUD_HANDOFF_PUBLISH_BLOCKED =
  'Publishing blocked. No scoreboard gamertags were extracted. You may confirm this VOD belongs to the verified handoff match based on the scoreboard from the live stream, team names on screen, and schedule binding.';

export const EMPTY_HUD_NEXT_ACTION_DESCRIPTION =
  'No scoreboard gamertags were extracted. You may confirm this VOD belongs to the verified handoff match based on the scoreboard from the live stream, team names on screen, and schedule binding.';

export const LEAGUE_MATCH_NEEDS_MATCH_CHIP = 'Needs match';

export const VERIFIED_LIVE_HANDOFF_SUMMARY =
  'LSC verified this game live, then linked the Twitch VOD automatically.';

export const VERIFIED_LIVE_HANDOFF_BANNER_TITLE =
  'Verified live handoff · VOD linked · Pending commissioner review';

export const VERIFIED_LIVE_HANDOFF_PUBLISH_BLOCKED =
  'Publish is blocked until commissioner review is complete.';

export const VERIFIED_LIVE_HANDOFF_CONFIRM_TITLE = 'Confirm live-verified match';

export const VERIFIED_LIVE_HANDOFF_CONFIRM_DESCRIPTION =
  'This game was verified live by LSC. Review the VOD scoreboard and tags before approval.';

export const VERIFIED_LIVE_HANDOFF_VERIFY_STEP =
  'Live verification is complete. Confirm the VOD evidence before approval.';

export const VERIFIED_LIVE_HANDOFF_CONFIRM_CTA = 'Confirm live-verified match';

export const VERIFIED_HANDOFF_MATCH_FALLBACK = 'Scheduled matchup linked';
